
import { Routes, CanActivateFn, Router, CanMatchFn, UrlSegment } from '@angular/router';
import { inject } from '@angular/core';
import { DeviceDataStore } from './services/device-data.store';
import { deviceReadyResolver } from './features/device/device-ready.resolver';
import { SignIn } from './auth/sign-in/sign-in';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';
import { deviceConnectionGuard } from './core/guards/device-connection.guard';

const identityRedirectGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const deviceId = route.paramMap.get('deviceId') ?? '';
  if (!deviceId) {
    return router.createUrlTree(['/device']);
  }
  return router.createUrlTree(['/device'], {
    queryParams: { deviceId },
    queryParamsHandling: 'merge',
  });
};

/** Require a loaded Device Identity for deeper /device child routes */
const requireDeviceIdentity: CanMatchFn = (_route, segments: UrlSegment[]) => {
  const router = inject(Router);
  const store = inject(DeviceDataStore);

  const url = '/' + segments.map(s => s.path).join('/');
  if (url.startsWith('/device/device-identity')) return true;

  const hasIdentity = !!store.identity();
  if (!hasIdentity) {
    const id = store.deviceId?.();
    return router.createUrlTree(['/device/device-identity'], {
      queryParams: id ? { deviceId: id } : undefined,
    });
  }
  return true;
};

export const routes: Routes = [

    // ===== Manual Crypto (Encrypt/Decrypt) =====
  {
    path: 'crypto',
    loadComponent: () => import('./features/manual-crypto/manual-crypto.component').then(m => m.ManualCryptoComponent),
    canActivate: [authGuard, roleGuard],
    data: { breadcrumb: 'Crypto' },
    children: [
      {
        path: '',
        redirectTo: 'encrypt',
        pathMatch: 'full',
      },
      {
        path: 'encrypt',
        loadComponent: () => import('./features/manual-crypto/encrypt/encrypt.component').then(m => m.EncryptComponent),
        data: { breadcrumb: 'Encrypt' },
      },
      {
        path: 'decrypt',
        loadComponent: () => import('./features/manual-crypto/decrypt/decrypt.component').then(m => m.DecryptComponent),
        data: { breadcrumb: 'Decrypt' },
      },
    ],
  },
  // ===== Telemetry History Standalone Page =====
  {
    path: 'telemetry-history',
    loadComponent: () => import('./features/device/telemetry-history/telemetry-history.page').then(m => m.TelemetryHistoryPage),
    canActivate: [authGuard, roleGuard],
    resolve: { ready: deviceReadyResolver },
    data: { breadcrumb: 'Telemetry History' },
  },
  // ===== Authentication Routes =====

      {
        path: 'sign-in',
        component: SignIn,
        data: { hideHeader: true, hideSidebar: true, hideLoader: true },
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./auth/forgot-password/forgot-password').then(m => m.ForgotPassword),
        data: { hideHeader: true, hideSidebar: true },
      },
      {
        path: 'sso-handshake',
        loadComponent: () => import('./auth/sso-handshake/sso-handshake').then(m => m.SsoHandshakeComponent),
        data: { hideHeader: true, hideSidebar: true },
      },
      {
        path: 'access-denied',
        loadComponent: () => import('./features/access-denied/access-denied').then(m => m.AccessDenied),
        // Intentionally NOT guarded by authGuard/roleGuard — this is the
        // destination for users who fail the role check, so it must always
        // be reachable regardless of role. hideHeader is set (matching
        // sign-in/forgot-password/sso-handshake) so a denied user isn't
        // shown the app header/Profile menu, which would just bounce them
        // straight back here via roleGuard.
        data: { hideHeader: true, hideSidebar: true, breadcrumb: 'Access Denied' },
      },
      
  // ===== Top-level feature routes (Protected) =====
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/dashboard').then(m => m.Dashboard),
    canActivate: [authGuard, roleGuard],
    data: { breadcrumb: 'Dashboard' },
  },
  {
    path: 'profile',
    loadComponent: () =>
      import('./features/profile/profile').then(m => m.Profile),
    canActivate: [authGuard, roleGuard],
    data: { breadcrumb: 'Profile' },
  },

  // ===== Normalize legacy deep-link to /device with query params =====
  {
    path: 'devices/:deviceId/identity',
    loadComponent: () => import('./features/device/device').then(m => m.Device),
    canActivate: [authGuard, roleGuard, identityRedirectGuard],
    pathMatch: 'full',
    data: { breadcrumb: 'Device Identity' },
  },

  // ===== Device feature shell with children (Protected) =====
  {
    path: 'device',
    data: { breadcrumb: 'Device' },
    canActivate: [authGuard, roleGuard],
    loadComponent: () => import('./features/device/device').then(m => m.Device),

    children: [
      {
        path: 'device-identity',
        loadComponent: () => import('./features/device/device-identity/device-identity')
          .then(m => m.DeviceIdentity),
        resolve: { ready: deviceReadyResolver },
        canActivate: [deviceConnectionGuard],
        // runGuardsAndResolvers removed to avoid redundant re-runs
        data: { breadcrumb: 'Device Identity' },
      },
      {
        path: 'device-twin',
        loadComponent: () => import('./features/device/device-twin/device-twin')
          .then(m => m.DeviceTwin),
        resolve: { ready: deviceReadyResolver },
        canActivate: [deviceConnectionGuard],
        data: { breadcrumb: 'Device Twin' },
      },
      {
        path: 'telemetry',
        loadComponent: () => import('./features/device/telemetry/telemetry')
          .then(m => m.Telemetry),
        resolve: { ready: deviceReadyResolver },
        canActivate: [deviceConnectionGuard],
        data: { breadcrumb: 'Telemetry' },
      },
      {
        path: 'direct-method',
        loadComponent: () => import('./features/device/direct-method/direct-method')
          .then(m => m.DirectMethod),
        resolve: { ready: deviceReadyResolver },
        canActivate: [deviceConnectionGuard],
        data: { breadcrumb: 'Direct Method' },
      },
      {
        path: 'cloud-to-device-message',
        loadComponent: () => import('./features/device/cloud-to-device-message/cloud-to-device-message')
          .then(m => m.CloudToDeviceMessage),
        resolve: { ready: deviceReadyResolver },
        canActivate: [deviceConnectionGuard],
        data: { breadcrumb: 'Cloud-to-Device Message' },
      },
    ],
  },

  // ===== Server feature shell with children (Protected) =====
  {
    path: 'server',
    data: { breadcrumb: 'Server' },
    canActivate: [authGuard, roleGuard],
    loadComponent: () => import('./features/server/server').then(m => m.Server),

    children: [
      {
        path: 'message',
        loadComponent: () => import('./features/server/message/message')
          .then(m => m.Message),
        data: { breadcrumb: 'Message' },
      },
      {
        path: 'user-notification',
        loadComponent: () => import('./features/server/user-notification/user-notification')
          .then(m => m.UserNotificationComponent),
        data: { breadcrumb: 'User Notification' },
      },
      {
        path: 'exceptions',
        loadComponent: () => import('./features/server/exceptions/exceptions.component')
          .then(m => m.ExceptionsComponent),
        data: { breadcrumb: 'Exceptions' },
      },
    ],
  },

  // ===== Help (Protected) =====
  {
    path: 'help',
    loadComponent: () => import('./features/help/help').then(m => m.Help),
    canActivate: [authGuard, roleGuard],
    data: { breadcrumb: 'Help' },
  },

  // ===== Redirects =====
  { path: '', pathMatch: 'full', redirectTo: '/sign-in' },

  // ===== 404 Not Found =====
  {
    path: '**',
    loadComponent: () => import('./features/not-found/not-found').then(m => m.NotFound),
    // hideHeader/hideSidebar so an authenticated non-admin who mistypes a
    // URL doesn't see the app chrome (name/initials, Profile/Logout, sidebar
    // nav) — same exposure closed on /access-denied, reachable via any typo.
    data: { hideHeader: true, hideSidebar: true, breadcrumb: 'Not Found' },
  },
];
